import { fetchSpeechTopics } from '@services/speechTopicsService';
import {
  buildTimestamp,
  containsReplacementChar,
  downloadTextFile,
  parseCsvFileWithEncodingFallback,
  unparseCsvWithBom,
} from '../../shared/csvUtils';
import { ensureLettersOnly, parseLocale, parsePopulation } from '../../shared/csvParsing';

// -----------------------
// Import (parse)
// -----------------------

const DEFAULT_TYPE = 'topicBasic';

const REQUIRED_HEADERS = ['text'];


const TYPE_ALLOWED_MESSAGE =
  'Allowed (case-insensitive): Basic, Advanced, Medium, Question, topicBasic, topicAdvanced, topicMedium, question.';

const POPULATION_ALLOWED_MESSAGE =
  'Allowed (case-insensitive): adult/adults, child/children. Use comma to separate values.';

const TYPE_UI_TO_DB = {
  basic: 'topicBasic',
  advanced: 'topicAdvanced',
  medium: 'topicMedium',
  question: 'question',
};

const TYPE_DB_CANONICAL = {
  topicbasic: 'topicBasic',
  topicadvanced: 'topicAdvanced',
  topicmedium: 'topicMedium',
  question: 'question',
};

const parseType = (value) => {
  const raw = ensureLettersOnly(value, 'type');
  if (!raw) return DEFAULT_TYPE;

  const token = raw.toLowerCase();
  const fromUi = TYPE_UI_TO_DB[token];
  if (fromUi) return fromUi;
  const fromDb = TYPE_DB_CANONICAL[token];
  if (fromDb) return fromDb;

  throw new Error(`Invalid type "${raw}". ${TYPE_ALLOWED_MESSAGE}`);
};

// parsePopulation is shared in csvParsing

const parseRow = (row, rowNumber) => {
  const text = String(row?.text ?? '').trim();
  if (!text) return { error: `Row ${rowNumber}: missing text` };

  try {
    return {
      value: {
        text,
        type: parseType(row?.type),
        locale: parseLocale(row?.locale),
        population: parsePopulation(row?.population),
      },
    };
  } catch (e) {
    return { error: `Row ${rowNumber}: ${e.message}` };
  }
};

export function parseSpeechTopicsCSV(file) {
  return parseCsvFileWithEncodingFallback({
    file,
    requiredHeaders: REQUIRED_HEADERS,
    parseRow,
    shouldRetryWithNextEncoding: (items) => (items || []).some((i) => containsReplacementChar(i?.text)),
  });
}

// -----------------------
// Export (download)
// -----------------------

// buildTimestamp + downloadTextFile are shared in csvUtils

export async function exportSpeechTopicsCSV() {
  const allTopics = await fetchSpeechTopics();
  if (!allTopics?.length) {
    throw new Error('No topics to export');
  }

  const rows = allTopics.map((t) => ({
    text: t?.text ?? '',
    type: t?.type ?? '',
    locale: t?.locale ?? '',
    population: Array.isArray(t?.population) ? t.population.join(',') : '',
  }));

  const csv = unparseCsvWithBom({
    rows,
    columns: ['text', 'type', 'locale', 'population'],
  });

  const filename = `novotalk_speech_topics_${buildTimestamp()}.csv`;
  downloadTextFile({ content: csv, filename, mimeType: 'text/csv;charset=utf-8;' });

  return { filename, count: rows.length };
}
